<template>
  <div v-if="isVisible" class="fixed inset-0 z-50 flex items-center justify-center">
    <!-- Backdrop -->
    <div 
      class="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
      @click="handleBackdropClick"
    ></div>
    
    <!-- Modal -->
    <div class="relative bg-[#1F1F1F] rounded-xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-[#333]">
        <div class="flex items-center gap-3">
          <!-- Icon based on type -->
          <div :class="iconClasses" class="w-8 h-8 rounded-full flex items-center justify-center">
            <svg v-if="type === 'success'" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <svg v-else-if="type === 'error'" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            <svg v-else-if="type === 'warning'" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <svg v-else class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h3 :class="titleClasses" class="text-lg font-semibold">
            {{ title }}
          </h3>
        </div>
        <button 
          @click="close"
          class="text-gray-400 hover:text-white transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <!-- Content -->
      <div class="p-6">
        <p class="text-gray-300 leading-relaxed whitespace-pre-line">
          {{ message }}
        </p>
      </div>
      
      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t border-[#333]">
        <button 
          v-if="showCancelButton"
          @click="handleCancel"
          class="px-4 py-2 text-gray-300 hover:text-white transition-colors"
        >
          {{ cancelText }}
        </button>
        <button 
          @click="handleConfirm"
          :class="confirmButtonClasses"
          class="px-6 py-2 rounded-lg font-medium transition-colors"
        >
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PopupModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'info',
      validator: value => ['success', 'error', 'warning', 'info'].includes(value)
    },
    title: {
      type: String,
      default: 'Notification'
    },
    message: {
      type: String,
      required: true
    },
    confirmText: {
      type: String,
      default: 'OK'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    showCancelButton: {
      type: Boolean,
      default: false
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    }
  },
  emits: ['confirm', 'cancel', 'close'],
  computed: {
    iconClasses() {
      const baseClasses = 'w-8 h-8 rounded-full flex items-center justify-center';
      switch (this.type) {
        case 'success':
          return `${baseClasses} bg-green-500`;
        case 'error':
          return `${baseClasses} bg-red-500`;
        case 'warning':
          return `${baseClasses} bg-yellow-500`;
        default:
          return `${baseClasses} bg-blue-500`;
      }
    },
    titleClasses() {
      switch (this.type) {
        case 'success':
          return 'text-green-400';
        case 'error':
          return 'text-red-400';
        case 'warning':
          return 'text-yellow-400';
        default:
          return 'text-blue-400';
      }
    },
    confirmButtonClasses() {
      const baseClasses = 'px-6 py-2 rounded-lg font-medium transition-colors';
      switch (this.type) {
        case 'success':
          return `${baseClasses} bg-green-500 hover:bg-green-600 text-white`;
        case 'error':
          return `${baseClasses} bg-red-500 hover:bg-red-600 text-white`;
        case 'warning':
          return `${baseClasses} bg-yellow-500 hover:bg-yellow-600 text-black`;
        default:
          return `${baseClasses} bg-[#F5BE32] hover:bg-[#E5AE22] text-black`;
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    handleConfirm() {
      this.$emit('confirm');
      this.close();
    },
    handleCancel() {
      this.$emit('cancel');
      this.close();
    },
    handleBackdropClick() {
      if (this.closeOnBackdrop) {
        this.close();
      }
    }
  }
}
</script> 